import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api, ApiError, apiUrl } from './api'
import { useApp } from './store'
import type { Member } from './types'

export function useH5Realtime() {
  const { member, setMember } = useApp()
  const client = useQueryClient()

  useEffect(() => {
    if (!member?.id) return

    let stopped = false
    let needsVisibleSync = false
    let lastVisibleSyncAt = 0
    let lastAuthCheckAt = 0
    let memberTimer = 0
    let orderTimer = 0
    let leaderboardTimer = 0
    let memberRefreshRunning = false
    let source: EventSource | null = null

    const visible = () => document.visibilityState === 'visible'
    const expireSession = () => {
      stopped = true
      source?.close()
      setMember(null)
    }
    const deferWhenHidden = () => {
      if (visible()) return false
      needsVisibleSync = true
      return true
    }
    const refreshMember = async () => {
      if (stopped || memberRefreshRunning || deferWhenHidden()) return
      memberRefreshRunning = true
      try {
        const updated = await api<Member>('/api/h5/me')
        if (!stopped) setMember(updated)
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) expireSession()
      } finally {
        memberRefreshRunning = false
      }
    }
    const scheduleMemberRefresh = () => {
      if (deferWhenHidden()) return
      window.clearTimeout(memberTimer)
      memberTimer = window.setTimeout(() => void refreshMember(), 100)
    }
    const scheduleOrderRefresh = () => {
      if (deferWhenHidden()) return
      window.clearTimeout(orderTimer)
      orderTimer = window.setTimeout(() => {
        void client.invalidateQueries({ queryKey: ['orders'], refetchType: 'active' })
        void client.invalidateQueries({ queryKey: ['recharge-order'], refetchType: 'active' })
      }, 150)
    }
    const scheduleLeaderboardRefresh = () => {
      if (deferWhenHidden()) return
      window.clearTimeout(leaderboardTimer)
      leaderboardTimer = window.setTimeout(() => {
        void client.invalidateQueries({ queryKey: ['leaderboard'], refetchType: 'active' })
      }, 250 + Math.floor(Math.random() * 500))
    }
    const syncVisibleState = () => {
      if (!visible()) {
        needsVisibleSync = true
        return
      }
      const now = Date.now()
      if (!needsVisibleSync && now - lastVisibleSyncAt < 1000) return
      needsVisibleSync = false
      lastVisibleSyncAt = now
      scheduleMemberRefresh()
      void client.invalidateQueries({ queryKey: ['orders'], refetchType: 'active' })
      void client.invalidateQueries({ queryKey: ['recharge-order'], refetchType: 'active' })
      void client.invalidateQueries({ queryKey: ['point-ledgers'], refetchType: 'active' })
      void client.invalidateQueries({ queryKey: ['leaderboard'], refetchType: 'active' })
      void client.invalidateQueries({ queryKey: ['lottery-status'], refetchType: 'active' })
      void client.invalidateQueries({ queryKey: ['h5-checkin-status'], refetchType: 'active' })
    }

    source = new EventSource(apiUrl('/api/h5/events'), { withCredentials: true })
    source.addEventListener('ready', syncVisibleState)
    source.addEventListener('member-assets-changed', () => {
      scheduleMemberRefresh()
      if (!deferWhenHidden()) {
        void client.invalidateQueries({ queryKey: ['point-ledgers'], refetchType: 'active' })
        void client.invalidateQueries({ queryKey: ['lottery-status'], refetchType: 'active' })
        void client.invalidateQueries({ queryKey: ['h5-checkin-status'], refetchType: 'active' })
      }
    })
    source.addEventListener('order-changed', scheduleOrderRefresh)
    source.addEventListener('leaderboard-changed', scheduleLeaderboardRefresh)
    source.addEventListener('session-invalidated', expireSession)
    source.addEventListener('error', () => {
      const now = Date.now()
      if (!visible() || now - lastAuthCheckAt < 15_000) return
      lastAuthCheckAt = now
      void refreshMember()
    })
    document.addEventListener('visibilitychange', syncVisibleState)
    window.addEventListener('pageshow', syncVisibleState)
    window.addEventListener('focus', syncVisibleState)

    return () => {
      stopped = true
      source?.close()
      window.clearTimeout(memberTimer)
      window.clearTimeout(orderTimer)
      window.clearTimeout(leaderboardTimer)
      document.removeEventListener('visibilitychange', syncVisibleState)
      window.removeEventListener('pageshow', syncVisibleState)
      window.removeEventListener('focus', syncVisibleState)
    }
  }, [client, member?.id, setMember])
}
